/**
 * Now-Playing page — focused view of the current track with a larger
 * cover, metadata, and quick links (Stats, Discord, etc).
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Clock, User } from "lucide-react";
import { usePlayer } from "../state/player";
import { CoverImg } from "../components/CoverImg";
import { OwnerBadge } from "../components/OwnerBadge";
import { fmtDuration } from "../lib/format";
import { useNowPlaying } from "../hooks/useQueue";

export const NowPlaying: React.FC = () => {
  const queue = usePlayer((s) => s.queue);
  const index = usePlayer((s) => s.index);
  const currentTime = usePlayer((s) => s.currentTime);
  const track = queue[index];

  // Pull the server-side NowPlaying for Discord sync info.
  const serverNp = useNowPlaying();

  useEffect(() => {
    document.title = track
      ? `${track.metadataTitle ?? track.name} · Memes & Shit`
      : "Now Playing";
  }, [track]);

  if (!track) {
    return (
      <div className="card p-10 text-center">
        <p className="text-sm text-gray-500">Nothing is playing right now.</p>
        <Link to="/" className="btn btn-primary mt-4 inline-flex">
          Build a shuffle
        </Link>
      </div>
    );
  }

  const remaining = (track.durationSec ?? 0) - currentTime;

  return (
    <div className="grid gap-8 md:grid-cols-[320px,1fr]">
      <CoverImg track={track} className="aspect-square w-full" rounded="rounded-3xl" />
      <div className="space-y-4">
        <div>
          <p className="text-xs uppercase tracking-wider text-gray-500">Now playing</p>
          <h1 className="mt-1 text-3xl font-bold leading-tight">
            {track.metadataTitle ?? track.name}
          </h1>
          <p className="mt-1 text-lg text-gray-600 dark:text-gray-300">
            {track.metadataArtist ?? "Unknown artist"}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <OwnerBadge owner={track.owner} />
            <span className="text-xs text-gray-500">
              <User className="inline h-3 w-3" /> submitted by {track.owner}
            </span>
          </div>
        </div>

        <dl className="grid gap-3 sm:grid-cols-2">
          <div className="card p-3">
            <dt className="text-xs text-gray-500">Duration</dt>
            <dd className="text-lg font-semibold">{fmtDuration(track.durationSec)}</dd>
          </div>
          <div className="card p-3">
            <dt className="text-xs text-gray-500">
              <Clock className="inline h-3 w-3" /> Remaining
            </dt>
            <dd className="text-lg font-semibold">{fmtDuration(remaining)}</dd>
          </div>
          <div className="card p-3">
            <dt className="text-xs text-gray-500">Position in queue</dt>
            <dd className="text-lg font-semibold">
              {index + 1} / {queue.length}
            </dd>
          </div>
          <div className="card p-3">
            <dt className="text-xs text-gray-500">Discord status</dt>
            <dd className="text-sm">
              {serverNp.data ? (
                <span className="text-green-600">✓ synced</span>
              ) : (
                <span className="text-gray-500">— not synced</span>
              )}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
};
